/**
 * Appels du référentiel d'exercices (`ARCHITECTURE_V2.md` §1.1 et §4.1).
 *
 * 🔴 **Toutes les routes d'écriture sont adressées par `clientId`, jamais par
 * l'`id` entier** (§2.3-b). C'est ce qui permet de composer hors ligne sans
 * place-titre : le client tire l'identité **avant** d'écrire, donc une routine
 * créée dans la même minute (étape 5) sait déjà vers quoi pointer.
 *
 * ⚠️ **`crypto.randomUUID()` et rien d'autre.** Un identifiant dérivé du nom
 * ferait collision entre deux exercices homonymes créés sur deux appareils, et
 * un compteur local ferait collision avec lui-même après une réinstallation.
 * L'API est disponible partout où l'application tourne (contexte sécurisé
 * obligatoire — HTTPS, §9.3).
 */

import type {
  ExerciseDTO,
  ExerciseInput,
  ExerciseSummaryDTO,
  UpdateExerciseBody,
} from '../../../shared/types';
import { apiFetch } from './client';

/**
 * Clé de cache de la **réplique** : la liste complète, sans instructions.
 *
 * 🔴 **Une seule entrée de cache pour tout l'écran**, et c'est le fond de la
 * recherche hors ligne (§3.1). La Bibliothèque ne requête pas `['exercises',
 * terme]` : elle lit cette liste-ci et la filtre en mémoire
 * (`lib/localSearch.ts`). Une clé par terme aurait rendu la recherche
 * impossible sans réseau — un terme jamais tapé en ligne n'a rien en cache.
 */
export const EXERCISES_KEY = ['exercises'] as const;

/** Clé du détail d'un exercice — mis en cache à la consultation, comme les images. */
export function exerciseKey(clientId: string) {
  return ['exercise', clientId] as const;
}

/**
 * La liste complète — ≈ 300 Ko pour 873 exercices, **sans les instructions**
 * (571 Ko à elles seules). Servie en une fois et comprimée par Caddy.
 */
export function fetchExercises(): Promise<ExerciseSummaryDTO[]> {
  return apiFetch<ExerciseSummaryDTO[]>('/api/exercises');
}

/** Le détail, instructions comprises. Le seul appel qui les transporte. */
export function fetchExercise(clientId: string): Promise<ExerciseDTO> {
  return apiFetch<ExerciseDTO>(`/api/exercises/${encodeURIComponent(clientId)}`);
}

/**
 * Création **ou** remplacement — un `PUT`, parce que le client possède déjà
 * l'identité (§2.3-b). Idempotent par construction : rejouer réécrit la même
 * ligne au même endroit.
 */
export function putExercise(clientId: string, body: ExerciseInput): Promise<ExerciseDTO> {
  return apiFetch<ExerciseDTO>(`/api/exercises/${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Modification partielle — le favori, sans avoir à porter tout l'exercice. */
export function patchExercise(
  clientId: string,
  body: UpdateExerciseBody,
): Promise<ExerciseDTO> {
  return apiFetch<ExerciseDTO>(`/api/exercises/${encodeURIComponent(clientId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteExercise(clientId: string): Promise<void> {
  return apiFetch<void>(`/api/exercises/${encodeURIComponent(clientId)}`, { method: 'DELETE' });
}

/* ==========================================================================
 * Médias
 * ======================================================================= */

/**
 * URL d'une vue d'un exercice.
 *
 * ⚠️ **Servie par Caddy sur `/exercises/*`, pas par l'API.** Ce sont des
 * fichiers statiques déposés par `infra/fetch-exercises.sh` : les faire passer
 * par Fastify les ferait transiter par Node pour rien, et surtout le service
 * worker les met en cache **à la consultation** (§1.1) — ce qu'il ne fait
 * jamais pour `/api/*`.
 *
 * ⚠️ `mediaKey` vient du serveur et n'a jamais été saisi par personne : les
 * routes d'écriture ne l'acceptent pas. Il est tout de même encodé, parce
 * qu'une URL se construit en encodant, pas en faisant confiance.
 */
export function exerciseImageUrl(mediaKey: string, index: number): string {
  return `/exercises/${encodeURIComponent(mediaKey)}/${String(index)}.jpg`;
}
